// Demo-mode mirror of backend/php/src/PatientRecordValidator.php. Only ever runs on the
// fictional demo chart. Keep check semantics aligned with the server rules.
export function ageOn(dob, today = new Date()) {
  const birth = new Date(`${dob}T12:00:00`);
  return today.getFullYear() - birth.getFullYear() - ((today.getMonth() < birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) ? 1 : 0);
}
const escaped = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function validateRecord(note, patient, attachedDx = [], templateKind = 'clinical', templateFields = [], context = {}) {
  const text = Object.values(note).join('\n');
  const flags = [];
  const add = (code, severity, title, message, evidence = [], suggestion = null) => {
    const flag = { code, severity, title, message, evidence };
    if (suggestion) flag.suggestion = suggestion;
    flags.push(flag);
  };

  const patientFull = `${patient.first_name} ${patient.last_name}`.toLowerCase();
  const wrong = [];
  (context.patients || []).forEach(o => {
    const name = `${o.first_name} ${o.last_name}`.toLowerCase().trim();
    if (name && name !== patientFull && name.length > 5 && new RegExp(`\\b${escaped(name)}\\b`, 'i').test(text)) wrong.push(name);
  });
  const distinctWrong = [...new Set(wrong)];
  if (distinctWrong.length) {
    const evidence = [{ label: 'Chart patient', value: `${patient.first_name} ${patient.last_name}`, tab: 'Overview' }];
    if (distinctWrong.length === 1 && !new RegExp(escaped(patientFull), 'i').test(text)) {
      add('wrong-patient', 'high', 'Possible wrong patient referenced', `The draft names “${distinctWrong[0]}” but this chart belongs to ${patient.first_name} ${patient.last_name}. Verify the patient before signing.`, evidence, { kind: 'replace_text', fields: 'all', before: distinctWrong[0], after: patientFull, label: 'Replace the referenced name with this chart’s patient name' });
    } else {
      add('wrong-patient', 'high', 'Possible wrong patient referenced', `The draft references another patient’s name: ${distinctWrong.join(', ')}. Verify the patient before signing.`, evidence);
    }
  }

  [...text.matchAll(/\b(\d{1,3})\s*(?:years? old|year-old|y\/o)\b/gi)].forEach(m => {
    if (Number(m[1]) !== ageOn(patient.dob)) add('age', 'review', 'Age mismatch', `The documented age (${m[1]}) differs from the date of birth.`, [{ label: 'Date of birth', value: `${patient.dob} (age ${ageOn(patient.dob)})`, tab: 'Overview' }]);
  });

  (patient.diagnoses || []).filter(d => d.status !== 'resolved' && d.status !== 'inactive').forEach(d => {
    ['knee', 'leg', 'hip', 'arm', 'shoulder', 'ankle', 'eye', 'ear', 'hand', 'foot', 'wrist'].forEach(part => {
      [['left', 'right'], ['right', 'left']].forEach(([side, other]) => {
        if (new RegExp(`\\b${side} ${part}\\b`, 'i').test(d.description) && new RegExp(`\\b${other} ${part}\\b`, 'i').test(text)) add(`laterality-${part}`, 'review', 'Laterality mismatch', `The note mentions ${other} ${part}; the active problem list documents ${side} ${part}. Confirm whether both findings are intended.`, [{ label: 'Active diagnosis', value: `${d.code} — ${d.description}`, tab: 'Diagnoses' }]);
      });
    });
    if (new RegExp(`\\b(?:denies|no history of)\\s+(?:history of\\s+)?${escaped(d.description)}\\b`, 'i').test(text)) add(`history-${d.code}`, 'review', 'Problem list conflict', `The note denies ${d.description}, which is on the active problem list.`, [{ label: 'Active diagnosis', value: `${d.code} — ${d.description}`, tab: 'Diagnoses' }]);
  });

  const cognitive = (patient.diagnoses || []).filter(d => ['active', 'chronic'].includes(d.status) && /dementia|alzheimer|major neurocognitive|cognitive impairment|delirium/i.test(d.description));
  if (cognitive.length && /\b(alert and oriented|fully alert|no cognitive)/i.test(text)) {
    add('mental-status-consistency', 'review', 'Orientation statement vs. cognitive history', 'The note describes the patient as alert and oriented. The chart lists an active cognitive diagnosis. This is a documentation-consistency check, not a determination that the note is wrong — reconcile the wording or document the change.', cognitive.map(d => ({ label: 'Active cognitive diagnosis', value: d.description, tab: 'Diagnoses' })));
  }

  const plans = Object.entries(note).filter(([k]) => /plan|recommendation|medications?|treatment|instruction|order|follow/i.test(k)).map(([, v]) => v).join('\n');
  (patient.allergies || []).forEach(a => {
    if (new RegExp(`\\b${escaped(a.substance)}\\b`, 'i').test(plans)) add(`allergy-${a.substance.toLowerCase()}`, 'high', 'Documented allergy mentioned in plan', `The treatment fields mention ${a.substance}; the chart records this allergy.`, [{ label: 'Recorded allergy', value: `${a.substance} — ${a.reaction || 'reaction not documented'}`, tab: 'Medications' }]);
  });
  (patient.medications || []).forEach(m => {
    if (m.status === 'discontinued' && new RegExp(`\\b(?:continue|start|resume|restart)\\s+${escaped(m.name)}\\b`, 'i').test(plans)) add(`medication-${m.id}`, 'review', 'Medication status conflict', `${m.name} is discontinued in the medication list. Confirm the intended order.`, [{ label: 'Medication list', value: `${m.name} ${m.dose} — discontinued`, tab: 'Medications' }]);
    plans.split(/(?<=[.!?\n])/).forEach(sentence => {
      if (new RegExp(`\\b(?:start|begin|continue|restart|resume|increase|decrease|hold)\\s+${escaped(m.name)}\\b`, 'i').test(sentence) && !/\d+(\.\d+)?\s*(mg|mcg|g|ml|units?|mmol)\b/i.test(sentence)) {
        if (!flags.some(f => f.code === `med-dose-${m.id}`)) add(`med-dose-${m.id}`, 'informational', 'Medication order may be incomplete', `The plan adjusts ${m.name} without a dose in that sentence.`, [{ label: 'Medication list', value: `${m.name} ${m.dose} ${m.frequency}`, tab: 'Medications' }]);
      }
    });
  });

  const directives = patient.advance_directives || '';
  if ((patient.advance_directive_on_file || /\b(dnr|do not resuscitate|do not transfer|comfort (care|only)|hospice)\b/i.test(directives)) && /\b(intubat\w+|full code|cpr\b|resuscitat\w+|icu transfer|aggressive (treatment|measures))/i.test(plans)) {
    add('advance-directive', 'review', 'Plan vs. documented wishes', 'The plan may conflict with documented advance directives or goals of care. This may require verification.', [{ label: 'Advance directives', value: directives.trim() || 'On file (see documents)', tab: 'Overview' }]);
  }

  const known = {};
  [...attachedDx, ...(patient.diagnoses || [])].forEach(d => { if (d.code) known[d.code.toUpperCase()] = d.description || null; });
  Object.entries(note).forEach(([field, value]) => {
    const hits = [...String(value).matchAll(/\b([A-Z][A-Z0-9]{2}(?:\.[A-Z0-9]{1,4})?)\s*[-–—:]\s*([^\n]{5,140})/gu)];
    hits.forEach(hit => {
      const code = hit[1].toUpperCase();
      if (known[code]) {
        const official = known[code];
        const found = hit[2].trim();
        if (found.toLowerCase() !== official.toLowerCase()) add(`icd-text-${code}`, 'review', 'ICD-10 description differs from the code', `The note pairs ${code} with “${found}”; the selected code reads “${official}”. Align the description with the code.`, [{ label: 'Official description', value: `${code} — ${official}`, tab: 'Diagnoses' }], { kind: 'replace_text', fields: [field], before: hit[0], after: `${code} — ${official}`, label: 'Replace with the official ICD-10 description' });
      }
    });
    const seen = new Set();
    String(value).split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 25).forEach(line => {
      const key = line.toLowerCase();
      if (seen.has(key)) add(`duplicate-line-${field}`, 'informational', 'Duplicate line in section', `“${line.slice(0, 80)}” appears twice in ${field}.`, [], { kind: 'remove_duplicate_line', fields: [field], before: line, after: '', label: 'Remove the repeated line (keep the first)' });
      seen.add(key);
    });
  });

  attachedDx.forEach(dx => {
    const code = dx.code || '';
    const desc = dx.description || '';
    const words = desc.toLowerCase().replace(/[^a-z ]/gi, ' ').split(/\s+/).filter(w => w.length >= 4 && !['unspecified', 'other', 'with', 'without'].includes(w));
    const mentioned = code && new RegExp(escaped(code), 'i').test(text) || words.some(w => text.toLowerCase().includes(w));
    if (!mentioned && code) add(`dx-unreferenced-${code}`, 'informational', 'Attached diagnosis not discussed', `${code} (${desc}) is attached but not referenced in the note body. Consider documenting the rationale or removing the attachment.`, [{ label: 'Attached diagnosis', value: `${code} — ${desc}`, tab: 'Diagnoses' }]);
  });

  templateFields.forEach(field => {
    if (field in note && !String(note[field]).trim()) add(`missing-${field}`, 'informational', 'Section left blank', `“${field}” is empty. Complete it or record why it is not applicable.`, []);
  });

  return [...new Map(flags.map(f => {
    const fingerprint = JSON.stringify([f.code, f.message, note, patient.record_version]);
    if (f.suggestion) f.suggestion = { ...f.suggestion, id: `${f.code}:${(f.suggestion.before || '').length}:${fingerprint.length}` };
    return [f.code, { ...f, fingerprint }];
  })).values()];
}

/** Demo-mode equivalent of the server-side applySuggestedFix. */
export function applySuggestedFixDemo(flags, fingerprint, suggestionId, note) {
  const flag = flags.find(f => f.fingerprint === fingerprint);
  if (!flag) throw new Error('This alert no longer matches the draft. Re-validate first.');
  const s = flag.suggestion;
  if (!s || s.id !== suggestionId) throw new Error('No safe automatic correction exists for this alert.');
  const fields = s.fields === 'all' ? Object.keys(note) : s.fields;
  let applied = false;
  const next = { ...note };
  fields.forEach(field => {
    if (!(field in next)) return;
    const current = String(next[field]);
    if (s.kind === 'replace_text' && current.toLowerCase().includes(s.before.toLowerCase())) {
      next[field] = current.replace(new RegExp(escaped(s.before), 'gi'), s.after);
      applied = true;
    } else if (s.kind === 'remove_duplicate_line') {
      let seen = false, removed = false;
      const lines = current.split(/\r?\n/).filter(line => {
        if (line.trim().toLowerCase() === s.before.trim().toLowerCase()) {
          if (!seen) { seen = true; return true; }
          removed = true; return false;
        }
        return true;
      });
      if (removed) { next[field] = lines.join('\n'); applied = true; }
    }
  });
  if (!applied) throw new Error('The draft changed since this correction was computed. Re-validate first.');
  return next;
}
